import React, { useState } from 'react'

import { Download } from 'lucide-react'
import { DateTime } from 'luxon'

import { ZoneConnection } from '@/core/api/entities/zone/zoneConnection'
import { useTranslation } from '@/core/i18n/useTranslation'
import { ConnectionChart } from '@/features/zones/components/ConnectionChart'
import { transformConnectionData } from '@/features/zones/utils/common'
import { Button } from '@/shared/components/atoms/Button'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/shared/components/atoms/Select'

interface ConnectionStatusTabProps {
	connections: ZoneConnection[]
	isLoading?: boolean
	error?: Error | null
	onDownloadReport: () => void
	onDateRangeChange: (startDate: DateTime, endDate: DateTime) => void
}

type DateFilterOption = 'all' | 'last7' | 'last30' | 'custom'

export const ConnectionStatusTab: React.FC<ConnectionStatusTabProps> = ({
	connections,
	isLoading = false,
	error = null,
	onDownloadReport,
	onDateRangeChange,
}) => {
	const [dateFilter, setDateFilter] = useState<DateFilterOption>('last7')
	const { t } = useTranslation('common')
	const handleDateFilterChange = (value: DateFilterOption) => {
		setDateFilter(value)

		if (onDateRangeChange) {
			const now = DateTime.now()
			switch (value) {
				case 'last7':
					onDateRangeChange(now.minus({ days: 7 }), now)
					break
				case 'last30':
					onDateRangeChange(now.minus({ days: 30 }), now)
					break
				case 'all':
				default:
					onDateRangeChange(now.minus({ months: 3 }), now)
					break
			}
		}
	}

	// Sort connections by date (most recent first) and transform for chart
	const sortedConnections = [...connections].sort((a, b) => {
		return (
			DateTime.fromISO(b.date).toMillis() - DateTime.fromISO(a.date).toMillis()
		)
	})
	const connectionData =
		sortedConnections.length > 0
			? transformConnectionData(sortedConnections)
			: []

	return (
		<div className="space-y-6">
			{/* Error State */}
			{error && (
				<div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
					<p className="text-sm text-red-400">
						{t('entities.zone.connection_report_view.status.error', {
							error: error.message,
						})}
					</p>
				</div>
			)}

			{/* Header with filters */}
			<div className="flex items-center justify-between">
				<Select
					value={dateFilter}
					onValueChange={(value) =>
						handleDateFilterChange(value as DateFilterOption)
					}
				>
					<SelectTrigger className="w-[200px] bg-input border rounded-lg">
						<SelectValue placeholder="Select date range" />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">
							{t('entities.zone.connection_report_view.status.all_dates')}
						</SelectItem>
						<SelectItem value="last7">
							{t('entities.zone.connection_report_view.status.last_7_days')}
						</SelectItem>
						<SelectItem value="last30">
							{t('entities.zone.connection_report_view.status.last_30_days')}
						</SelectItem>
						<SelectItem value="custom">
							{t('entities.zone.connection_report_view.status.custom_range')}
						</SelectItem>
					</SelectContent>
				</Select>

				{onDownloadReport && (
					<Button
						variant="default"
						onClick={onDownloadReport}
						className="rounded-full bg-white text-black hover:bg-gray-100"
					>
						{t('entities.zone.connection_report.download_report')}
						<Download className="ml-2 h-4 w-4" />
					</Button>
				)}
			</div>

			{/* Connection Chart */}
			{!error && (
				<ConnectionChart
					data={connectionData}
					mode="hourly"
					showToggle={false}
					isLoading={isLoading}
				/>
			)}

			{/* Empty state */}
			{!isLoading && !error && connectionData.length === 0 && (
				<div className="text-center py-12">
					<p className="text-muted-foreground">
						{t('entities.zone.connection_report_view.no_data')}
					</p>
				</div>
			)}

			{/* Info footer */}
			{!error && (
				<div className="flex items-start gap-4 text-xs text-muted-foreground pt-4 border-t border-border">
					<div className="flex items-center gap-2">
						<div className="h-3 w-3 rounded bg-status-success" />
						<span>
							{t('entities.zone.connection_report_view.connection_stable')}
						</span>
					</div>
					<div className="flex items-center gap-2">
						<div className="h-3 w-3 rounded bg-status-warning" />
						<span>
							{t('entities.zone.connection_report_view.poor_connection')}
						</span>
					</div>
					<div className="flex items-center gap-2">
						<div className="h-3 w-3 rounded bg-shade" />
						<span>
							{t('entities.zone.connection_report_view.no_connection')}
						</span>
					</div>
				</div>
			)}
		</div>
	)
}
